import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { notificationService } from '../lib/notification-service';
import { pushNotificationService } from '../lib/push-notification-service';
import { useActivityTracking } from './useActivityTracking';

export interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  parent_comment_id?: string;
  created_at: string;
  updated_at: string;
  likes_count: number;
  replies_count: number;
  is_pinned: boolean;
  // User details
  user: {
    id: string;
    username: string;
    full_name: string;
    avatar_url: string;
  };
  // Replies (for nested comments)
  replies?: Comment[];
  // Like status for current user
  is_liked?: boolean;
}

export function useComments(postId: string) {
  const { user } = useAuth();
  const { trackPostInteraction } = useActivityTracking();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load comments for a post
  const loadComments = useCallback(async () => {
    if (!postId) return;

    try {
      setLoading(true);
      setError(null);

      const { data: commentsData, error: fetchError } = await supabase
        .from('comments')
        .select(`
          id,
          post_id,
          user_id,
          content,
          parent_comment_id,
          created_at,
          updated_at,
          likes_count,
          replies_count,
          is_pinned,
          user:users!id(
            id,
            username,
            full_name,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .is('parent_comment_id', null) // Only top-level comments
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      if (commentsData) {
        const transformedComments = commentsData.map((comment: any) => ({
          ...comment,
          user: comment.user[0] || {}, // Take first element from user array
          is_liked: false // Add default value
        }));

        // Load likes status for current user if authenticated
        if (user) {
          const commentIds = transformedComments.map(comment => comment.id);
          const { data: likes } = await supabase
            .from('comment_likes')
            .select('comment_id')
            .eq('user_id', user.id)
            .in('comment_id', commentIds);

          const likedCommentIds = new Set(likes?.map(like => like.comment_id) || []);

          const commentsWithLikes = transformedComments.map(comment => ({
            ...comment,
            is_liked: likedCommentIds.has(comment.id)
          }));

          setComments(commentsWithLikes);
        } else {
          setComments(transformedComments);
        }
      }
    } catch (err) {
      console.error('Error loading comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [postId, user]);

  // Load replies for a comment
  const loadReplies = useCallback(async (commentId: string): Promise<Comment[]> => {
    try {
      const { data: repliesData, error: fetchError } = await supabase
        .from('comments')
        .select(`
          id,
          post_id,
          user_id,
          content,
          parent_comment_id,
          created_at,
          updated_at,
          likes_count,
          replies_count,
          is_pinned,
          user:users!id(
            id,
            username,
            full_name,
            avatar_url
          )
        `)
        .eq('parent_comment_id', commentId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      if (repliesData) {
        const transformedReplies = repliesData.map((reply: any) => ({
          ...reply,
          user: reply.user[0] || {}, // Take first element from user array
          is_liked: false // Add default value
        }));

        // Load likes status for current user if authenticated
        if (user) {
          const commentIds = transformedReplies.map((comment: any) => comment.id);
          const { data: likes } = await supabase
            .from('comment_likes')
            .select('comment_id')
            .eq('user_id', user.id)
            .in('comment_id', commentIds);

          const likedCommentIds = new Set(likes?.map(like => like.comment_id) || []);

          return transformedReplies.map((comment: any) => ({
            ...comment,
            is_liked: likedCommentIds.has(comment.id)
          }));
        }

        return transformedReplies;
      }

      return [];
    } catch (err) {
      console.error('Error loading replies:', err);
      return [];
    }
  }, [user]);

  // Submit a new comment
  const submitComment = useCallback(async (
    content: string,
    parentCommentId?: string,
    postOwnerId?: string,
    postData?: any
  ) => {
    if (!user) throw new Error('User not authenticated');
    if (!content.trim()) throw new Error('Comment content is required');

    try {
      setSubmitting(true);
      setError(null);

      const { data, error } = await supabase
        .from('comments')
        .insert([{
          post_id: postId,
          user_id: user.id,
          content: content.trim(),
          parent_comment_id: parentCommentId || null
        }])
        .select(`
          id,
          post_id,
          user_id,
          content,
          parent_comment_id,
          created_at,
          updated_at,
          likes_count,
          replies_count,
          is_pinned,
          user:users!id(
            id,
            username,
            full_name,
            avatar_url
          )
        `)
        .single();

      if (error) throw error;

      // Track the comment activity
      if (postData) {
        await trackPostInteraction(
          postId,
          'comment',
          `Commented: ${content.substring(0, 50)}${content.length > 50 ? '...' : ''}`,
          postData.caption || '',
          postData.username || ''
        );
      }

      // Send notification to post owner
      if (postOwnerId) {
        await notificationService.createNotification(
          postOwnerId,
          'comment',
          `${user.user_metadata?.username || 'Someone'} commented on your post`,
          postId
        );

        // Send push notification
        await pushNotificationService.sendCommentNotification(
          postOwnerId,
          user.user_metadata?.username || 'Someone',
          postId,
          content
        );
      }

      // Reload comments to get updated counts
      await loadComments();

      return data;
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError('Failed to submit comment');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [user, postId, trackPostInteraction, loadComments]);

  // Like/unlike a comment
  const toggleCommentLike = useCallback(async (commentId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Check if already liked
      const { data: existingLike } = await supabase
        .from('comment_likes')
        .select('id')
        .eq('user_id', user.id)
        .eq('comment_id', commentId)
        .single();

      if (existingLike) {
        // Unlike
        const { error } = await supabase
          .from('comment_likes')
          .delete()
          .eq('user_id', user.id)
          .eq('comment_id', commentId);

        if (error) throw error;

        // Update likes count
        const { error: updateError } = await supabase
          .rpc('decrement_comment_likes', { comment_id: commentId });

        if (updateError) throw updateError;
      } else {
        // Like
        const { error } = await supabase
          .from('comment_likes')
          .insert([{
            user_id: user.id,
            comment_id: commentId
          }]);

        if (error) throw error;

        // Update likes count
        const { error: updateError } = await supabase
          .rpc('increment_comment_likes', { comment_id: commentId });

        if (updateError) throw updateError;
      }

      // Reload comments to get updated state
      await loadComments();
    } catch (err) {
      console.error('Error toggling comment like:', err);
      throw err;
    }
  }, [user, loadComments]);

  // Pin/unpin a comment (for post owners)
  const toggleCommentPin = useCallback(async (commentId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Get current pin status
      const { data: comment } = await supabase
        .from('comments')
        .select('is_pinned')
        .eq('id', commentId)
        .single();

      if (!comment) throw new Error('Comment not found');

      // Toggle pin status
      const { error } = await supabase
        .from('comments')
        .update({ is_pinned: !comment.is_pinned })
        .eq('id', commentId);

      if (error) throw error;

      // Reload comments
      await loadComments();
    } catch (err) {
      console.error('Error toggling comment pin:', err);
      throw err;
    }
  }, [user, loadComments]);

  // Delete a comment
  const deleteComment = useCallback(async (commentId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id); // Only allow users to delete their own comments

      if (error) throw error;

      // Reload comments
      await loadComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
      throw err;
    }
  }, [user, loadComments]);

  // Load comments on mount and when postId changes
  useEffect(() => {
    if (postId) {
      loadComments();
    }
  }, [postId]);

  // Set up real-time subscription for new comments
  const loadCommentsRef = useRef(loadComments);
  loadCommentsRef.current = loadComments;

  useEffect(() => {
    if (!postId) return;

    const subscription = supabase
      .channel(`comments:${postId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `post_id=eq.${postId}`
        },
        () => {
          // Reload comments when changes occur
          loadCommentsRef.current();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [postId]);

  return {
    comments,
    loading,
    submitting,
    error,
    loadComments,
    loadReplies,
    submitComment,
    toggleCommentLike,
    toggleCommentPin,
    deleteComment
  };
}
